import { Select } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';

import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { useCheckOption } from '@/App/tiles/Option/QueueContext.tsx';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput/index.tsx';
import type { LegId, OptionBarrier, PossibleProperty } from '@/store/option/state.ts';

type TriggerTypeProps = {
  tileId: string;
  legId: LegId;
  barrier?: OptionBarrier;
  numberOfBarrier?: number;
  possibleNatures?: PossibleProperty<OptionBarrier['nature']>;
  possibleTypes?: PossibleProperty<OptionBarrier['type']>;
};

function TriggerLevelInput({
  tileId,
  barrier,
  numberOfBarrier = 1,
  legId,
}: Readonly<Omit<TriggerTypeProps, 'possibleNatures' | 'possibleTypes'>>) {
  const { sgwtWebAnalytics } = useSgwtWidgets();
  const [checkOption] = useCheckOption();

  function handleBarrierLevelChange(level: number | undefined) {
    sgwtWebAnalytics?.trackEvent('Option', 'trigger level changed', String(level));

    checkOption({
      tileId,
      legProperties: {
        legId,
        [`barrier${numberOfBarrier}`]: { level: level },
      },
    });
  }

  return (
    <SgNumberInput
      valuePosition="start"
      selectAllOnFocus
      value={barrier?.level ?? undefined}
      allowNegative={false}
      maxDecimals={9}
      className="bg-primary-alt border-end"
      onChange={handleBarrierLevelChange}
      size="sm"
      data-e2e="leverage-ratio"
    />
  );
}

function TriggerTypeNatureSelects({
  tileId,
  barrier,
  numberOfBarrier = 1,
  possibleNatures,
  possibleTypes,
  legId,
}: Readonly<TriggerTypeProps>) {
  const { sgwtWebAnalytics } = useSgwtWidgets();
  const [checkOption] = useCheckOption();

  function handleBarrierTypeChange(triggerType: OptionBarrier['type']) {
    sgwtWebAnalytics?.trackEvent('Option', 'trigger type changed', String(triggerType));

    checkOption({
      tileId,
      legProperties: {
        legId,
        [`barrier${numberOfBarrier}`]: { type: triggerType },
      },
    });
  }

  function handleBarrierNatureChange(triggerNature: OptionBarrier['nature']) {
    sgwtWebAnalytics?.trackEvent('Option', 'trigger nature changed', String(triggerNature));

    checkOption({
      tileId,
      legProperties: {
        legId,
        [`barrier${numberOfBarrier}`]: { nature: triggerNature },
      },
    });
  }

  return (
    <>
      <Select
        data-e2e="trigger-types-select"
        className="form-select form-select-sm bg-primary-alt"
        selectedItemValue={barrier?.type ?? possibleTypes?.default}
        itemsAsObjects={possibleTypes?.possible ?? []}
        onChange={handleBarrierTypeChange}
      />
      <Select
        data-e2e="trigger-nature-select"
        className="form-select form-select-sm bg-primary-alt"
        selectedItemValue={barrier?.nature ?? possibleNatures?.default}
        itemsAsObjects={possibleNatures?.possible ?? []}
        onChange={handleBarrierNatureChange}
      />
    </>
  );
}

export const FirstTrigger1 = ({ tileId, legId }: { tileId: string; legId: LegId }) => {
  const barrier1 = useOptionSelector((option) => option.properties.legs.find((l) => l.legId === legId)?.barrier1);
  return barrier1 ? <TriggerLevelInput tileId={tileId} legId={legId} barrier={barrier1} numberOfBarrier={1} /> : null;
};

export const SecondTrigger1 = ({ tileId, legId }: { tileId: string; legId: LegId }) => {
  const { barrier1, possibleNatures1, possibleTypes1 } = useOptionSelector((option) => {
    const leg = option.properties.legs.find((l) => l.legId === legId);
    return {
      barrier1: leg?.barrier1,
      possibleNatures1: leg?.barrier1?.possibleNatures,
      possibleTypes1: leg?.barrier1?.possibleTypes,
    };
  });
  return barrier1 ? (
    <TriggerTypeNatureSelects
      tileId={tileId}
      legId={legId}
      barrier={barrier1}
      numberOfBarrier={1}
      possibleNatures={possibleNatures1}
      possibleTypes={possibleTypes1}
    />
  ) : null;
};

export const FirstTrigger2 = ({ tileId, legId }: { tileId: string; legId: LegId }) => {
  const barrier2 = useOptionSelector((option) => option.properties.legs.find((l) => l.legId === legId)?.barrier2);
  return barrier2 ? <TriggerLevelInput tileId={tileId} legId={legId} barrier={barrier2} numberOfBarrier={2} /> : null;
};

export const SecondTrigger2 = ({ tileId, legId }: { tileId: string; legId: LegId }) => {
  const { barrier2, possibleNatures2, possibleTypes2 } = useOptionSelector((option) => {
    const leg = option.properties.legs.find((l) => l.legId === legId);
    return {
      barrier2: leg?.barrier2,
      possibleNatures2: leg?.barrier2?.possibleNatures,
      possibleTypes2: leg?.barrier2?.possibleTypes,
    };
  });
  return barrier2 ? (
    <TriggerTypeNatureSelects
      tileId={tileId}
      legId={legId}
      barrier={barrier2}
      numberOfBarrier={2}
      possibleNatures={possibleNatures2}
      possibleTypes={possibleTypes2}
    />
  ) : null;
};
